import {
  TableCell,
  Checkbox,
  Tooltip,
  IconButton,
  Autocomplete,
  TextField,
} from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import HistoryIcon from "@mui/icons-material/History";
import type {
  exchangeRateBody,
  CurrencyOption,
  breakdownProcess,
} from "@/api/breakdownExchangeRateService";
import { type partSKP } from "@/api/supplierService";
import { useMemo, useState, useEffect } from "react";
import { isValidDecimalInput, parseDecimal } from "@/utils/inputParse";

interface EditRowProps {
  formData: exchangeRateBody;
  setFormData: React.Dispatch<React.SetStateAction<exchangeRateBody>>;
  partDropdownOptions: partSKP[];
  currencyOptions: CurrencyOption[];
  errors: { [field: string]: string };
  isAdding: boolean;
  part?: {
    partId: number;
    partNumber: string;
    partName: string;
    ac: string;
    rt: string;
  };
  oemProcess: breakdownProcess[];
  spareProcess: breakdownProcess[];
  isOpenOEMOtherProcess: boolean;
  isOpenSpareOtherProcess: boolean;
}

const EditRow = ({
  formData,
  setFormData,
  partDropdownOptions,
  currencyOptions,
  errors,
  isAdding,
  part,
  oemProcess,
  spareProcess,
  isOpenOEMOtherProcess,
  isOpenSpareOtherProcess,
}: EditRowProps) => {
  const backgroundColor = "#fffbe6";
  const [priceInput, setPriceInput] = useState<string>(
    formData?.price ? formData.price.toString() : "",
  );
  const [OEMPriceInput, setOEMPriceInput] = useState<{
    [processId: number]: string;
  }>(
    formData?.oem_other_process
      ? formData.oem_other_process.reduce(
          (acc, process) => {
            acc[process.id] = process.cost.toString();
            return acc;
          },
          {} as { [processId: number]: string },
        )
      : {},
  );
  const [sparePriceInput, setSparePriceInput] = useState<{
    [processId: number]: string;
  }>(
    formData?.spare_other_process
      ? formData.spare_other_process.reduce(
          (acc, process) => {
            acc[process.id] = process.cost.toString();
            return acc;
          },
          {} as { [processId: number]: string },
        )
      : {},
  );

  const totalOEMOtherProcessCost = useMemo(() => {
    const total = (formData.oem_other_process || []).reduce(
      (sum, process) => sum + process.cost,
      0,
    );
    return parseDecimal(total, 2);
  }, [formData.oem_other_process]);

  const totalSpareOtherProcessCost = useMemo(() => {
    const total = (formData.spare_other_process || []).reduce(
      (sum, process) => sum + process.cost,
      0,
    );
    return parseDecimal(total, 2);
  }, [formData.spare_other_process]);

  useEffect(() => {
    setPriceInput(formData?.price ? formData.price.toString() : "");
  }, [formData?.price]);

  const selectedExchangeRate:
    | {
        exchange_rate: number;
        rounding: "ROUND" | "ROUND UP" | "ROUND DOWN";
        rounding_digit: number;
      }
    | undefined = useMemo(() => {
    return currencyOptions.find(
      (opt) =>
        opt.value.currency_id === formData?.currency_id &&
        opt.value.exchange_rate_supplier_id ===
          formData?.exchange_rate_supplier_id,
    )?.value;
  }, [
    formData?.currency_id,
    formData?.exchange_rate_supplier_id,
    currencyOptions,
  ]);

  const partPrice: number | null = useMemo(() => {
    if (priceInput === "") return null;
    if (selectedExchangeRate === undefined) return null;
    let result = parseFloat(priceInput) * selectedExchangeRate.exchange_rate;
    switch (selectedExchangeRate.rounding) {
      case "ROUND":
        result = parseDecimal(
          result.toString(),
          selectedExchangeRate.rounding_digit,
        );
        break;
      case "ROUND UP":
        result = parseDecimal(
          Math.ceil(
            result * Math.pow(10, selectedExchangeRate.rounding_digit),
          ) / Math.pow(10, selectedExchangeRate.rounding_digit),
          selectedExchangeRate.rounding_digit,
        );
        break;
      case "ROUND DOWN":
        result = parseDecimal(
          Math.floor(
            result * Math.pow(10, selectedExchangeRate.rounding_digit),
          ) / Math.pow(10, selectedExchangeRate.rounding_digit),
          selectedExchangeRate.rounding_digit,
        );
        break;
      default:
        result = parseDecimal(
          result.toString(),
          selectedExchangeRate.rounding_digit,
        );
        break;
    }
    return result;
  }, [priceInput, selectedExchangeRate]);

  const OEMPrice: number | null = useMemo(() => {
    if (partPrice === null) return null;
    if (totalOEMOtherProcessCost === null) return partPrice;
    return parseDecimal(partPrice + totalOEMOtherProcessCost, 2);
  }, [partPrice, totalOEMOtherProcessCost]);

  const sparePrice: number | null = useMemo(() => {
    if (partPrice === null) return null;
    if (totalSpareOtherProcessCost === null) return partPrice;
    return parseDecimal(partPrice + totalSpareOtherProcessCost, 2);
  }, [partPrice, totalSpareOtherProcessCost]);

  return (
    <>
      {/* Checkbox */}
      <TableCell
        sx={{
          position: "sticky",
          left: 0,
          zIndex: 101,
          width: 70,
          minWidth: 70,
          maxWidth: 70,
          textAlign: "center",
          backgroundColor: backgroundColor,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
        }}
      >
        <Checkbox checked={true} />
      </TableCell>
      {/* Action */}
      <TableCell
        sx={{
          position: "sticky",
          left: 70,
          zIndex: 101,
          width: 120,
          minWidth: 120,
          maxWidth: 120,
          textAlign: "center",
          backgroundColor: backgroundColor,
          borderRight: "1px solid #e0e0e0",
        }}
      >
        <Tooltip title="Flag" placement="top">
          <IconButton disabled={true}>
            <FlagIcon color={"disabled"} />
          </IconButton>
        </Tooltip>
        <Tooltip title="History" placement="top">
          <IconButton disabled={true}>
            <HistoryIcon color={"disabled"} />
          </IconButton>
        </Tooltip>
      </TableCell>
      {/* Part No. */}
      <TableCell
        sx={{
          position: "sticky",
          left: 190,
          zIndex: 101,
          width: 200,
          minWidth: 200,
          maxWidth: 200,
          textAlign: "left",
          backgroundColor: backgroundColor,
          borderRight: "1px solid #e0e0e0",
        }}
      >
        {isAdding ? (
          <Autocomplete
            fullWidth
            value={
              partDropdownOptions.find(
                (opt) => opt.part_id === formData?.part_id,
              ) || null
            }
            onChange={(_event, newValue) => {
              setFormData((prev) => ({
                ...prev,
                part_id: newValue ? newValue.part_id : 0,
              }));
            }}
            options={partDropdownOptions}
            getOptionLabel={(option) => option.part_number}
            isOptionEqualToValue={(option, value) =>
              option.part_id === value.part_id
            }
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "0.875rem",
                  },
                }}
                size="small"
                error={!!errors.part_id}
                helperText={errors.part_id}
              />
            )}
          />
        ) : (
          part?.partId || ""
        )}
      </TableCell>
      {/* Part Name */}
      <TableCell
        sx={{
          position: "sticky",
          left: 390,
          zIndex: 101,
          width: 200,
          minWidth: 200,
          maxWidth: 200,
          textAlign: "left",
          backgroundColor: backgroundColor,
          borderRight: "1px solid #e0e0e0",
        }}
      >
        {isAdding
          ? partDropdownOptions.find((opt) => opt.part_id === formData?.part_id)
              ?.part_name || ""
          : part?.partName || ""}
      </TableCell>
      {/* AC */}
      <TableCell
        sx={{
          position: "sticky",
          left: 590,
          zIndex: 101,
          width: 70,
          minWidth: 70,
          maxWidth: 70,
          textAlign: "center",
          backgroundColor: backgroundColor,
          borderRight: "1px solid #e0e0e0",
        }}
      >
        {isAdding
          ? partDropdownOptions.find((opt) => opt.part_id === formData?.part_id)
              ?.ac || ""
          : part?.ac || ""}
      </TableCell>
      {/* Routing */}
      <TableCell
        sx={{
          position: "sticky",
          left: 660,
          zIndex: 101,
          width: 90,
          minWidth: 90,
          maxWidth: 90,
          textAlign: "center",
          backgroundColor: backgroundColor,
          borderRight: "2px solid #e0e0e0",
        }}
      >
        {isAdding
          ? partDropdownOptions.find((opt) => opt.part_id === formData?.part_id)
              ?.rt || ""
          : part?.rt || ""}
      </TableCell>
      {/* OEM Price */}
      <TableCell
        sx={{
          minWidth: 150,
          maxWidth: 150,
          textAlign: "right",
          borderRight: "2px solid #e0e0e0",
          fontSize: "0.9rem",
          backgroundColor: backgroundColor,
        }}
      >
        {OEMPrice !== null ? OEMPrice : "0.00"}
      </TableCell>
      {/* Spare Price */}
      <TableCell
        sx={{
          minWidth: 150,
          maxWidth: 150,
          textAlign: "right",
          borderRight: "2px solid #e0e0e0",
          fontSize: "0.9rem",
          backgroundColor: backgroundColor,
        }}
      >
        {sparePrice !== null ? sparePrice : "0.00"}
      </TableCell>{" "}
      {/* Currency */}
      <TableCell
        sx={{
          minWidth: 300,
          maxWidth: 300,
          textAlign: "left",
          borderRight: "1px solid #e0e0e0",
          backgroundColor: backgroundColor,
        }}
      >
        <Autocomplete
          fullWidth
          value={
            currencyOptions.find(
              (opt) =>
                opt.value.currency_id === formData?.currency_id &&
                opt.value.exchange_rate_supplier_id ===
                  formData?.exchange_rate_supplier_id,
            ) || null
          }
          onChange={(_event, newValue) => {
            setFormData((prev) => ({
              ...prev,
              currency_id: newValue ? newValue.value.currency_id : 0,
              exchange_rate_supplier_id: newValue
                ? newValue.value.exchange_rate_supplier_id
                : 0,
              currency_code: newValue ? newValue.label : "",
            }));
          }}
          options={currencyOptions}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) =>
            option.value.currency_id === value.value.currency_id &&
            option.value.exchange_rate_supplier_id ===
              value.value.exchange_rate_supplier_id
          }
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: "0.875rem",
                },
              }}
              size="small"
              error={!!errors.currency_id}
              helperText={errors.currency_id}
            />
          )}
        />
      </TableCell>{" "}
      {/* Exchange Rate */}
      <TableCell
        sx={{
          minWidth: 150,
          maxWidth: 150,
          textAlign: "right",
          borderRight: "1px solid #e0e0e0",
          fontSize: "0.9rem",
          backgroundColor: backgroundColor,
        }}
      >
        {selectedExchangeRate?.exchange_rate || "0"}
      </TableCell>
      {/* Price */}
      <TableCell
        sx={{
          minWidth: 150,
          maxWidth: 150,
          textAlign: "right",
          borderRight: "1px solid #e0e0e0",
          fontSize: "0.9rem",
          backgroundColor: backgroundColor,
        }}
      >
        <TextField
          value={priceInput}
          onChange={(e) => {
            const val = e.target.value;
            if (isValidDecimalInput(val, 2)) {
              setPriceInput(val);
              setFormData((prev) => ({
                ...prev,
                price: val === "" ? 0 : parseDecimal(val, 2),
              }));
            }
          }}
          size="small"
          fullWidth
          sx={{
            "& input": { textAlign: "right" },
          }}
          error={!!errors.price}
          helperText={errors.price}
        />
      </TableCell>
      {/* Part Price */}
      <TableCell
        sx={{
          minWidth: 150,
          maxWidth: 150,
          textAlign: "right",
          borderRight: "1px solid #e0e0e0",
          fontSize: "0.9rem",
          backgroundColor: backgroundColor,
        }}
      >
        {partPrice !== null ? partPrice : "0.00"}
      </TableCell>
      {/* OEM Price */}
      <TableCell
        sx={{
          minWidth: 200,
          maxWidth: 200,
          textAlign: "right",
          borderRight: "1px solid #e0e0e0",
          fontSize: "0.9rem",
          backgroundColor: backgroundColor,
        }}
      >
        {totalOEMOtherProcessCost || "0.00"}
      </TableCell>
      {isOpenOEMOtherProcess &&
        oemProcess.map((process) => (
          <TableCell
            key={process.id}
            sx={{
              minWidth: 150,
              maxWidth: 150,
              textAlign: "right",
              borderRight: "1px solid #e0e0e0",
              fontSize: "0.9rem",
              backgroundColor: backgroundColor,
            }}
          >
            <TextField
              value={OEMPriceInput[process.id] || ""}
              onChange={(e) => {
                const val = e.target.value;
                if (isValidDecimalInput(val, 2)) {
                  setOEMPriceInput((prev) => ({
                    ...prev,
                    [process.id]: val,
                  }));
                  setFormData((prev) => {
                    const oemProcesses = prev.oem_other_process || [];
                    const exists = oemProcesses.some(
                      (p) => p.id === process.id,
                    );
                    return {
                      ...prev,
                      oem_other_process: exists
                        ? oemProcesses.map((p) =>
                            p.id === process.id
                              ? {
                                  ...p,
                                  cost: val === "" ? 0 : parseDecimal(val, 2),
                                }
                              : p,
                          )
                        : [
                            ...oemProcesses,
                            {
                              id: process.id,
                              cost: val === "" ? 0 : parseDecimal(val, 2),
                            },
                          ],
                    };
                  });
                }
              }}
              size="small"
              fullWidth
              sx={{
                "& input": { textAlign: "right" },
              }}
            />
          </TableCell>
        ))}
      {/* Spare Price */}
      <TableCell
        sx={{
          minWidth: 200,
          maxWidth: 200,
          textAlign: "right",
          borderRight: "1px solid #e0e0e0",
          fontSize: "0.9rem",
          backgroundColor: backgroundColor,
        }}
      >
        {totalSpareOtherProcessCost || "0.00"}
      </TableCell>
      {isOpenSpareOtherProcess &&
        spareProcess.map((process) => (
          <TableCell
            key={process.id}
            sx={{
              minWidth: 150,
              maxWidth: 150,
              textAlign: "right",
              borderRight: "1px solid #e0e0e0",
              fontSize: "0.9rem",
              backgroundColor: backgroundColor,
            }}
          >
            <TextField
              value={sparePriceInput[process.id] || ""}
              onChange={(e) => {
                const val = e.target.value;
                if (isValidDecimalInput(val, 2)) {
                  setSparePriceInput((prev) => ({
                    ...prev,
                    [process.id]: val,
                  }));
                  setFormData((prev) => {
                    const spareProcesses = prev.spare_other_process || [];
                    const exists = spareProcesses.some(
                      (p) => p.id === process.id,
                    );
                    return {
                      ...prev,
                      spare_other_process: exists
                        ? spareProcesses.map((p) =>
                            p.id === process.id
                              ? {
                                  ...p,
                                  cost: val === "" ? 0 : parseDecimal(val, 2),
                                }
                              : p,
                          )
                        : [
                            ...spareProcesses,
                            {
                              id: process.id,
                              cost: val === "" ? 0 : parseDecimal(val, 2),
                            },
                          ],
                    };
                  });
                }
              }}
              size="small"
              fullWidth
              sx={{
                "& input": { textAlign: "right" },
              }}
            />
          </TableCell>
        ))}
    </>
  );
};

export default EditRow;
